import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Badge from 'react-bootstrap/Badge';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Image from 'react-bootstrap/Image';
import Button from 'react-bootstrap/Button';
import api from '../utils/api';
import PaymentForm from '../components/PaymentForm';
import { getStatusConfig } from '../utils/orderStatusConstants';
import './Cards.css';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(parseFloat(value));
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('th-TH');
};

const formatShippingAddress = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    if (trimmed.startsWith('{')) {
      try {
        return formatShippingAddress(JSON.parse(trimmed));
      } catch (error) {
        console.warn('Failed to parse shipping address JSON:', trimmed, error);
        return trimmed;
      }
    }
    return trimmed;
  }

  if (typeof value === 'object') {
    const recipientName = [value.recipientName, value.recipientSurname]
      .filter(Boolean)
      .join(' ')
      .trim();
    const lines = [];
    if (recipientName) {
      lines.push(recipientName);
    }
    if (value.phone) {
      lines.push(`โทร: ${value.phone}`);
    }
    if (value.address) {
      lines.push(value.address);
    }
    return lines.join('\n');
  }

  return '';
};

const isInstallmentOrder = (order) => {
  if (!order) return false;
  const periods = Number(order.installment_periods) || 0;
  const monthlyPayment = Number(order.monthly_payment) || 0;
  const method = order.payment_method ? String(order.payment_method).toLowerCase() : '';

  if (method.includes('installment')) {
    return true;
  }

  if (method.includes('cash')) {
    return false;
  }

  if (periods > 1) {
    return true;
  }

  return periods >= 1 && monthlyPayment > 0;
};

export default function Cards() {
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [paymentModal, setPaymentModal] = useState({ show: false, installment: null });

  // Check if there's a pending payment for a specific installment
  const hasPendingPayment = (installmentId) => {
    if (!orderDetail?.payments || !Array.isArray(orderDetail.payments)) return false;
    return orderDetail.payments.some(
      p => p.installment_id === installmentId && p.status === 'pending'
    );
  };

  // Check if payment button should be shown for an installment
  const shouldShowPaymentButton = (installment) => {
    // Only show for installment number >= 2 (first installment is paid on order detail page)
    if (!installment || installment.installment_number < 2) {
      return false;
    }

    // Don't show if already paid
    if (installment.payment_status === 'paid') {
      return false;
    }

    // Don't show if there's a pending payment
    if (hasPendingPayment(installment.installment_id)) {
      return false;
    }

    // Check if current date is within 10 days before due date
    if (installment.payment_due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const dueDate = new Date(installment.payment_due_date);
      dueDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      // Calculate 10 days before due date
      const tenDaysBefore = new Date(dueDate);
      tenDaysBefore.setDate(tenDaysBefore.getDate() - 10);
      
      // Only show button if today is >= 10 days before due date and <= due date
      if (today < tenDaysBefore || today > dueDate) {
        return false;
      }
    }

    return true;
  };

  const handleOpenPaymentModal = (installment) => {
    setPaymentModal({ show: true, installment });
  };

  const handleClosePaymentModal = () => {
    setPaymentModal({ show: false, installment: null });
  };

  const handlePaymentSuccess = () => {
    // Refresh order detail after successful payment submission
    if (selectedOrderId) {
      const fetchOrderDetail = async () => {
        setIsDetailLoading(true);
        setDetailError('');
        try {
          const response = await api.get(`/api/orders/${selectedOrderId}`);
          setOrderDetail(response.data);
        } catch (error) {
          console.error('Error fetching order detail:', error);
          setDetailError(error.response?.data?.error || 'ไม่สามารถโหลดรายละเอียดคำสั่งซื้อได้');
        } finally {
          setIsDetailLoading(false);
        }
      };
      fetchOrderDetail();
    }
    setPaymentModal({ show: false, installment: null });
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setIsOrdersLoading(true);
      setOrdersError('');
      try {
        const response = await api.get('/api/customer/orders');
        const rawData = Array.isArray(response.data) ? response.data : [];
        
        // กรองเฉพาะ orders ที่มีสถานะ "จัดส่งสำเร็จ" (completed) และเป็น installment order
        const completedInstallmentOrders = rawData.filter(
          (order) =>
            order.order_status === 'completed' &&
            isInstallmentOrder(order)
        );

        if (completedInstallmentOrders.length === 0) {
          setOrders([]);
          setSelectedOrderId(null);
          setOrdersError(
            rawData.length > 0
              ? 'ยังไม่มีคำสั่งซื้อที่จัดส่งสำเร็จและอยู่ในแผนผ่อนชำระ'
              : 'ยังไม่มีคำสั่งซื้อ'
          );
          return;
        }

        setOrders(completedInstallmentOrders);
        setOrdersError('');
        setSelectedOrderId(completedInstallmentOrders[0].order_id);
      } catch (error) {
        console.error('Error fetching customer orders:', error);
        setOrdersError(error.response?.data?.error || 'ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้');
      } finally {
        setIsOrdersLoading(false);
      }
    };

    fetchOrders();
  }, []);

  useEffect(() => {
    if (!selectedOrderId) {
      setOrderDetail(null);
      return;
    }

    const fetchOrderDetail = async () => {
      setIsDetailLoading(true);
      setDetailError('');
      try {
        const response = await api.get(`/api/orders/${selectedOrderId}`);
        setOrderDetail(response.data);
      } catch (error) {
        console.error('Error fetching order detail:', error);
        setDetailError(error.response?.data?.error || 'ไม่สามารถโหลดรายละเอียดคำสั่งซื้อได้');
        setOrderDetail(null);
      } finally {
        setIsDetailLoading(false);
      }
    };

    fetchOrderDetail();
  }, [selectedOrderId]);

  return (
    <div className="cards-page">
      <Container>
        <header className="cards-contract__header-only">
          <div>
            <span className="cards-contract__eyebrow">การ์ดและแผนผ่อน</span>
            <h1>การ์ดที่ผูกไว้และสถานะการผ่อน</h1>
            <p className="text-muted">
              แสดงเฉพาะคำสั่งซื้อที่จัดส่งสำเร็จ เลือกคำสั่งซื้อเพื่อดูจำนวนงวด ยอดชำระ และรายละเอียดแต่ละงวดได้ในที่เดียว
            </p>
          </div>
          <div className="cards-contract__selector-shell">
            <Form.Select
              value={selectedOrderId ?? ''}
              onChange={(event) => setSelectedOrderId(event.target.value || null)}
              disabled={isOrdersLoading || orders.length === 0}
              className="cards-contract__selector"
            >
              {orders.length === 0 && <option value="">ยังไม่มีคำสั่งซื้อ</option>}
              {orders.map((order) => {
                const primaryItem = order.items?.[0];
                return (
                  <option key={order.order_id} value={order.order_id}>
                    #{order.order_id} • {primaryItem?.product_name || 'ไม่ระบุสินค้า'}
                  </option>
                );
              })}
            </Form.Select>
            <div className="cards-contract__selector-display">
              {isOrdersLoading ? (
                <span>กำลังโหลด...</span>
              ) : selectedOrderId ? (
                (() => {
                  const selectedOrder = orders.find((order) => order.order_id === Number(selectedOrderId));
                  const primaryItem = selectedOrder?.items?.[0];
                  const statusKey = orderDetail?.order_status;
                  const statusConfig = getStatusConfig(statusKey || '');
                  const isCancelled =
                    statusKey === 'cancelled' || statusKey === 'cancelled_by_customer';
                  const isPending = statusKey === 'pending';
                  const isAwaitingPayment = statusKey === 'awaiting_payment';
                  const isCompleted = statusKey === 'completed';
                  const badgeClasses = [
                    'cards-status-badge',
                    isCancelled ? 'cards-status-badge--cancelled' : '',
                    isPending ? 'cards-status-badge--pending' : '',
                    isAwaitingPayment ? 'cards-status-badge--awaiting' : '',
                    isCompleted ? 'cards-status-badge--completed' : ''
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <>
                      <div className="selector-product">
                        <Image
                          src={primaryItem?.product_image || 'https://via.placeholder.com/80'}
                          alt={primaryItem?.product_name || 'สินค้า'}
                          rounded
                        />
                        <div>
                          <strong>{primaryItem?.product_name || `คำสั่งซื้อ #${selectedOrder?.order_id || '-'}`}</strong>
                          <span>
                            {formatDate(selectedOrder?.order_date)} • {formatCurrency(selectedOrder?.total_amount)}
                          </span>
                        </div>
                      </div>
                      <Badge
                        bg={statusConfig.variant}
                        className={badgeClasses}
                      >
                        {statusConfig.label}
                      </Badge>
                    </>
                  );
                })()
              ) : (
                <span>เลือกคำสั่งซื้อ</span>
              )}
            </div>
          </div>
        </header>

        {ordersError && (
          <Alert variant="danger" className="cards-contract__alert">
            {ordersError}
          </Alert>
        )}

        {orderDetail && (
          <div className="cards-summary">
            <div className="cards-summary__item">
              <span className="label">ยอดรวมสัญญา</span>
              <strong>{formatCurrency(orderDetail.total_amount)}</strong>
            </div>
            <div className="cards-summary__item">
              <span className="label">จำนวนงวดทั้งหมด</span>
              <strong>{orderDetail.installment_periods || '-'}</strong>
            </div>
            <div className="cards-summary__item">
              <span className="label">งวดยอดปัจจุบัน</span>
              <strong>{formatCurrency(orderDetail.monthly_payment)}</strong>
            </div>
            <div className="cards-summary__item cards-summary__item--address">
              <span className="label">ที่อยู่จัดส่ง</span>
              <p className="cards-summary__address">
                {formatShippingAddress(orderDetail.shipping_address) || 'ไม่ได้ระบุที่อยู่จัดส่ง'}
              </p>
            </div>
          </div>
        )}

        {detailError && !isDetailLoading && (
          <Alert variant="danger" className="cards-contract__alert">
            {detailError}
          </Alert>
        )}

        {isDetailLoading ? (
          <div className="cards-contract__loading">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            <span>กำลังโหลดตารางผ่อนชำระ...</span>
          </div>
        ) : (
          <>
            {orderDetail?.installments?.length ? (
              <div className="cards-contract__table-wrapper">
                <div className="cards-contract__summary">
                  <Badge bg="light" text="dark">
                    เหลืออีก {orderDetail.installments.filter((item) => item.payment_status !== 'paid').length} งวด
                  </Badge>
                  {(() => {
                    const summaryStatus = orderDetail.order_status;
                    const summaryClasses = ['cards-status-badge'];
                    if (summaryStatus === 'cancelled' || summaryStatus === 'cancelled_by_customer') {
                      summaryClasses.push('cards-status-badge--cancelled');
                    }
                    if (summaryStatus === 'pending') {
                      summaryClasses.push('cards-status-badge--pending');
                    }
                    if (summaryStatus === 'awaiting_payment') {
                      summaryClasses.push('cards-status-badge--awaiting');
                    }
                    if (summaryStatus === 'completed') {
                      summaryClasses.push('cards-status-badge--completed');
                    }
                    return (
                      <Badge
                        bg={getStatusConfig(orderDetail.order_status).variant}
                        className={summaryClasses.join(' ')}
                      >
                        {getStatusConfig(orderDetail.order_status).label}
                      </Badge>
                    );
                  })()}
                </div>
                <Table responsive hover className="cards-contract__table">
                  <thead>
                    <tr>
                      <th>งวดที่</th>
                      <th>ยอดชำระ</th>
                      <th>กำหนดชำระ</th>
                      <th>วันที่ชำระ</th>
                      <th>สถานะ</th>
                      <th>จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetail.installments.map((item) => (
                      <tr key={item.installment_id}>
                        <td>{item.installment_number}</td>
                        <td>{formatCurrency(item.installment_amount)}</td>
                        <td>{formatDate(item.payment_due_date)}</td>
                        <td>{formatDate(item.payment_date)}</td>
                        <td>
                          <div>
                            <Badge bg={item.payment_status === 'paid' ? 'success' : 'secondary'}>
                              {item.payment_status === 'paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                            </Badge>
                            {hasPendingPayment(item.installment_id) && (
                              <Alert variant="info" className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                                <i className="bi bi-info-circle me-1"></i>
                                รอการอนุมัติจากแอดมิน
                              </Alert>
                            )}
                            {(() => {
                              const rejectedPayments = orderDetail.payments?.filter(
                                p => p.installment_id === item.installment_id && p.status === 'rejected'
                              ) || [];
                              
                              if (rejectedPayments.length > 0) {
                                // แสดงเฉพาะ rejection reason ล่าสุด
                                const latestRejection = rejectedPayments.sort((a, b) => 
                                  new Date(b.created_at) - new Date(a.created_at)
                                )[0];
                                
                                return (
                                  <Alert variant="danger" className="mt-2 mb-0" style={{ 
                                    fontSize: '0.85rem',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '0.375rem'
                                  }}>
                                    <div className="d-flex align-items-start">
                                      <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                                      <div className="flex-grow-1">
                                        <strong className="d-block mb-1">การชำระเงินถูกปฏิเสธ</strong>
                                        <span>{latestRejection.rejection_reason || 'ไม่มีเหตุผล'}</span>
                                      </div>
                                    </div>
                                  </Alert>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                        <td>
                          {shouldShowPaymentButton(item) && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleOpenPaymentModal(item)}
                            >
                              <i className="bi bi-qr-code me-1" />
                              ชำระเงิน
                            </Button>
                          )}
                          {item.installment_number === 1 && item.payment_status !== 'paid' && !hasPendingPayment(item.installment_id) && (
                            <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>
                              จ่ายที่หน้าคำสั่งซื้อ
                            </small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="cards-contract__payment-note" style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  background: 'rgba(34, 87, 185, 0.05)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(34, 87, 185, 0.1)'
                }}>
                  <p className="mb-0" style={{ 
                    fontSize: '0.9rem', 
                    color: 'rgba(20, 48, 101, 0.8)',
                    lineHeight: '1.6'
                  }}>
                    <i className="bi bi-info-circle me-2" style={{ color: 'rgba(34, 87, 185, 0.7)' }}></i>
                    <strong>หมายเหตุ:</strong> สามารถชำระเงินได้ก่อน 10 วันถึงกำหนดชำระ สำหรับงวดที่ 2 เป็นต้นไป
                  </p>
                </div>
              </div>
            ) : (
              <p className="cards-contract__empty">
                เลือกคำสั่งซื้อเพื่อดูตารางการผ่อนชำระ หรือยังไม่มีข้อมูลการผ่อนสำหรับคำสั่งซื้อนี้
              </p>
            )}
          </>
        )}

      </Container>

      <PaymentForm
        show={paymentModal.show}
        onHide={handleClosePaymentModal}
        orderId={selectedOrderId}
        installmentId={paymentModal.installment?.installment_id}
        installmentNumber={paymentModal.installment?.installment_number}
        amount={paymentModal.installment?.installment_amount}
        totalInstallments={orderDetail?.installment_periods}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}

